import React from 'react';

import {isInaccessibleInfo} from 'auth/RequestAccess';
import {MaybeRenderWithSignedURL} from 'external-files/SignedExtFileURLProxy';
import FilePreview from 'files/preview/FilePreview';
import BenchlingQuery from 'graphql-client/BenchlingQuery';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import {CenteredSpinner} from 'ui-library/Spinner';

import {GetScratchFile, GetScratchFileVariables} from './network/__generated/GetScratchFile';
import {GET_SCRATCH_FILE_QUERY} from './network/GetScratchFile';
import ScratchFileChip from './ScratchFileChip';
import {ScratchFileContent} from './ScratchFileTypes';

interface ScratchFilePaneContentProps {
  scratchFile: ScratchFileContent;
}

function ScratchFileUrlResolver({
  scratchFile,
  addHtmlContentTypeHeader,
  children,
}: {
  scratchFile: ScratchFileContent;
  addHtmlContentTypeHeader: boolean;
  children: (signedUrl: string) => JSX.Element;
}): JSX.Element {
  return (
    <MaybeRenderWithSignedURL
      addHtmlContentTypeHeader={addHtmlContentTypeHeader}
      externalFileId={scratchFile.externalFileId}
      url={scratchFile.downloadUrl!}
      renderWithSignedURL={(signedURL) => {
        if (signedURL === undefined) {
          return <CenteredSpinner />;
        }
        if (signedURL == null) {
          return <InlineBanner bannerType={InlineBannerType.ERROR} text="Error loading file." />;
        }
        return children(signedURL);
      }}
      owningEntityApiId={scratchFile.id}
    />
  );
}

export function renderFilePreview(scratchFile: ScratchFileContent): JSX.Element {
  const {mimeType} = scratchFile;
  if (scratchFile.externalFileId == null || scratchFile.downloadUrl == null) {
    return <ScratchFileChip scratchFile={scratchFile} />;
  }

  const isHtml = mimeType === 'text/html';
  const htmlOptions = isHtml ? {dangerouslyAllowScripts: true} : undefined;

  return (
    <ScratchFileUrlResolver scratchFile={scratchFile} addHtmlContentTypeHeader={isHtml}>
      {(signedUrl) => (
        <FilePreview
          url={signedUrl}
          mimeType={mimeType ?? ''}
          fileSize={scratchFile.size}
          filename={scratchFile.filename}
          htmlOptions={htmlOptions}
          fallback={<ScratchFileChip scratchFile={scratchFile} />}
        />
      )}
    </ScratchFileUrlResolver>
  );
}

export default function ScratchFilePaneContent({scratchFile}: ScratchFilePaneContentProps): JSX.Element {
  return renderFilePreview(scratchFile);
}

interface QueryingScratchFilePaneContentProps {
  fileId: string;
}

export function QueryingScratchFilePaneContent({fileId}: QueryingScratchFilePaneContentProps): JSX.Element {
  return (
    <BenchlingQuery<GetScratchFile, GetScratchFileVariables>
      query={GET_SCRATCH_FILE_QUERY}
      variables={{id: fileId}}
      renderLoading={() => <CenteredSpinner />}
    >
      {(data) => {
        if (isInaccessibleInfo(data.scratchFile)) {
          return (
            <InlineBanner bannerType={InlineBannerType.ERROR} text="You do not have access to this file." />
          );
        }

        return <ScratchFilePaneContent scratchFile={data.scratchFile} />;
      }}
    </BenchlingQuery>
  );
}
